import React, { useState, useEffect, FormEvent } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";

// Services & Types
import { OfferingService } from "services/offering.service";
import { EventService } from "services/event.service";
import { FundService } from "services/fund.service";
import {
  ISaveOffering,
  IEventResponse,
  IFundResponse,
} from "models/church.types";

export default function OfferingRegister() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingFunds, setLoadingFunds] = useState(false);

  const [events, setEvents] = useState<
    { value: number; label: string }[]
  >([]);

  const [funds, setFunds] = useState<IFundResponse[]>([]);

  const [errors, setErrors] = useState<{
    eventId?: string;
    amount?: string;
  }>({});

  // ============================================================
  // INITIALISATION
  // ============================================================

  const [offering, setOffering] = useState<
    ISaveOffering & { fundId?: number | null }
  >({
    amount: 0,
    offeringDate: new Date().toISOString(),
    description: "",
    eventId: 0,
    fundId: null,
  });

  // ============================================================
  // STYLE DU SELECT PROGRAMME
  // ============================================================

  const customSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "#0F172A",
      borderColor: state.isFocused ? "#D4AF37" : "#334155",
      borderRadius: "0.5rem",
      padding: "3px",
      minHeight: "45px",
      color: "white",
      boxShadow: "none",
      transition: "all 0.2s",
      "&:hover": {
        borderColor: "#D4AF37",
      },
    }),

    menu: (base: any) => ({
      ...base,
      backgroundColor: "#1E293B",
      border: "1px solid #334155",
      zIndex: 100,
    }),

    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused
        ? "#D4AF37"
        : "transparent",
      color: state.isFocused ? "#0F172A" : "white",
      padding: "10px",
      cursor: "pointer",
      fontSize: "0.875rem",
    }),

    singleValue: (base: any) => ({
      ...base,
      color: "white",
    }),

    input: (base: any) => ({
      ...base,
      color: "white",
    }),

    placeholder: (base: any) => ({
      ...base,
      color: "#64748b",
      fontSize: "0.875rem",
    }),

    indicatorSeparator: () => ({
      display: "none",
    }),
  };

  // ============================================================
  // CHARGEMENT DES DONNÉES
  // ============================================================

  useEffect(() => {
    const initData = async () => {
      setLoadingFunds(true);

      try {
        // ======================================================
        // 1. CHARGEMENT DES PROGRAMMES
        // ======================================================

        const eventsRes = await EventService.getAll(0, 1000);

        setEvents(
          (eventsRes.data || []).map(
            (e: IEventResponse) => ({
              value: e.id,
              label: `${e.title} (${new Date(
                e.eventDate,
              ).toLocaleDateString("fr-FR")})`,
            }),
          ),
        );

        // ======================================================
        // 2. CHARGEMENT DES CAISSES
        // ======================================================

        const fundsResult = await FundService.getAll(0, 100);

        setFunds(fundsResult.data || []);

        // ======================================================
        // 3. CHARGEMENT DE L'OFFRANDE EN MODE ÉDITION
        // ======================================================

        if (isEditMode && id) {
          const data = await OfferingService.getById(
            parseInt(id, 10),
          );

          setOffering({
            amount: data.amount,
            offeringDate: data.offeringDate,
            description: data.description || "",
            eventId: data.eventId,
            fundId: data.fundId ?? null,
          });
        }
      } catch (error) {
        console.error(
          "Erreur lors du chargement des données :",
          error,
        );

        toast.error(
          "Erreur lors du chargement des données",
        );

        if (isEditMode) {
          history.push("/admin/offerings");
        }
      } finally {
        setLoadingFunds(false);
      }
    };

    initData();
  }, [id, isEditMode, history]);

  // ============================================================
  // SOUMISSION DU FORMULAIRE
  // ============================================================

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // ==========================================================
    // VALIDATION
    // ==========================================================

    const newErrors: {
      eventId?: string;
      amount?: string;
    } = {};

    if (!offering.eventId) {
      newErrors.eventId =
        "Veuillez sélectionner un programme";
    }

    if (offering.amount <= 0) {
      newErrors.amount =
        "Le montant doit être supérieur à 0";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      // ========================================================
      // MODE ÉDITION
      // ========================================================

      if (isEditMode && id) {
        await OfferingService.update({
          ...offering,
          id: parseInt(id, 10),
        } as any);

        toast.success(
          "Offrande mise à jour avec succès",
        );
      }

      // ========================================================
      // MODE CRÉATION
      // ========================================================

      else {
        await OfferingService.create(offering);

        toast.success(
          "Offrande enregistrée avec succès",
        );
      }

      history.push("/admin/offerings");
    } catch (error) {
      console.error(
        "Erreur lors de l'enregistrement de l'offrande :",
        error,
      );

      toast.error(
        "Erreur lors de l'enregistrement.",
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="flex flex-col items-center justify-center mt-12 p-4 w-full min-h-screen">
      <div className="w-full max-w-2xl">

        <div className="bg-[#1E293B] border border-slate-700 shadow-2xl rounded-xl overflow-hidden">

          {/* ==================================================
              HEADER
          ================================================== */}

          <div className="bg-[#0F172A] px-6 py-4 border-b border-slate-700 flex justify-between items-center">

            <h6 className="text-white text-sm font-bold uppercase tracking-widest">

              <i className="fas fa-hand-holding-usd mr-2 text-[#D4AF37]"></i>

              {isEditMode
                ? "Éditer l'offrande"
                : "Nouvelle offrande"}

            </h6>

          </div>

          {/* ==================================================
              FORMULAIRE
          ================================================== */}

          <form
            onSubmit={handleSubmit}
            className="px-6 py-8 grid grid-cols-1 gap-6"
          >

            {/* ==================================================
                PROGRAMME
            ================================================== */}

            <div>

              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                Programme
              </label>

              <Select
                options={events}
                styles={customSelectStyles}
                value={
                  events.find(
                    (opt) =>
                      opt.value === offering.eventId,
                  ) || null
                }
                onChange={(selected: any) => {
                  setOffering({
                    ...offering,
                    eventId: selected?.value || 0,
                  });

                  if (errors.eventId) {
                    setErrors({
                      ...errors,
                      eventId: undefined,
                    });
                  }
                }}
                placeholder="Sélectionner un programme..."
                isClearable
              />

              {errors.eventId && (
                <span className="text-red-500 text-xs mt-1 ml-1 block">
                  {errors.eventId}
                </span>
              )}

            </div>

            {/* ==================================================
                MONTANT
            ================================================== */}

            <div>

              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                Montant
              </label>

              <input
                type="number"
                min="0"
                value={
                  offering.amount === 0
                    ? ""
                    : offering.amount
                }
                onChange={(e) => {
                  const val = e.target.value;

                  setOffering({
                    ...offering,
                    amount:
                      val === ""
                        ? 0
                        : Number(val),
                  });

                  if (errors.amount) {
                    setErrors({
                      ...errors,
                      amount: undefined,
                    });
                  }
                }}
                placeholder="0"
                className={`w-full bg-[#0F172A] border ${
                  errors.amount
                    ? "border-red-500"
                    : "border-slate-700"
                } rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none transition-all`}
              />

              {errors.amount && (
                <span className="text-red-500 text-xs mt-1 ml-1 block">
                  {errors.amount}
                </span>
              )}

            </div>

            {/* ==================================================
                CAISSE DESTINATION
            ================================================== */}

            <div>

              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">

                <i className="fas fa-wallet mr-2 text-[#D4AF37]"></i>

                Caisse destination

              </label>

              <div className="relative">

                <select
                  name="fundId"
                  value={offering.fundId ?? ""}
                  onChange={(e) =>
                    setOffering({
                      ...offering,
                      fundId: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                  disabled={loadingFunds}
                  className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all appearance-none cursor-pointer disabled:opacity-50"
                >

                  <option value="">
                    -- Aucune caisse (Non rattachée) --
                  </option>

                  {funds.map((fund) => (
                    <option
                      key={fund.id}
                      value={fund.id}
                    >
                      {fund.name}
                    </option>
                  ))}

                </select>

                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-400">

                  {loadingFunds ? (
                    <i className="fas fa-circle-notch animate-spin text-[#D4AF37]"></i>
                  ) : (
                    <i className="fas fa-chevron-down text-xs"></i>
                  )}

                </div>

              </div>

              <p className="text-slate-500 text-[11px] mt-1 italic ml-1">
                Le montant de l'offrande sera crédité sur cette caisse.
              </p>

            </div>

            {/* ==================================================
                DATE DE L'OFFRANDE
            ================================================== */}

            <div>

              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                Date de l'offrande
              </label>

              <input
                type="datetime-local"
                value={
                  offering.offeringDate
                    ? offering.offeringDate.slice(0, 16)
                    : ""
                }
                onChange={(e) =>
                  setOffering({
                    ...offering,
                    offeringDate: e.target.value
                      ? new Date(
                          e.target.value,
                        ).toISOString()
                      : new Date().toISOString(),
                  })
                }
                className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none transition-all [color-scheme:dark]"
              />

            </div>

            {/* ==================================================
                DESCRIPTION
            ================================================== */}

            <div>

              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                Description / Notes
              </label>

              <textarea
                value={offering.description || ""}
                onChange={(e) =>
                  setOffering({
                    ...offering,
                    description: e.target.value,
                  })
                }
                placeholder="Informations complémentaires ou référence du donateur..."
                className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none h-24 transition-all resize-none"
              />

            </div>

            {/* ==================================================
                BOUTONS
            ================================================== */}

            <div className="mt-6 flex justify-end gap-4 border-t border-slate-700 pt-6">

              <button
                type="button"
                onClick={() =>
                  history.push("/admin/offerings")
                }
                className="px-6 py-3 text-slate-400 hover:text-white font-bold text-xs uppercase transition-colors"
              >
                Annuler
              </button>

              <button
                type="submit"
                disabled={loading}
                className="bg-[#D4AF37] text-[#0F172A] px-10 py-3 rounded-lg font-black text-xs uppercase hover:scale-105 active:scale-95 transition-all shadow-md disabled:opacity-50 flex items-center"
              >

                {loading ? (
                  <>
                    <i className="fas fa-spinner animate-spin mr-2"></i>
                    Chargement...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i>
                    Enregistrer
                  </>
                )}

              </button>

            </div>

          </form>

        </div>

      </div>
    </div>
  );
}